import { useState } from "react";
import { colorPalette } from "@shared/config/colorsConfig";
import { typography } from '@shared/config/typographyConfig';
import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import { Text } from "@shared/components/text";
import { View, TextInput, StyleSheet, KeyboardTypeOptions, ReturnKeyTypeOptions } from "react-native";

export interface IInputProps {
    hasError?: boolean;
    errorMessage?: string;
    value?: string;
    label?: string;
    unit?: string;
    keyboardType?: KeyboardTypeOptions;
    returnKeyType?: ReturnKeyTypeOptions;
    editable?: boolean;
    onFocus?: () => void;
    onChangeText?: (text: string) => void;
}

export const Input = ({
    hasError,
    errorMessage,
    value,
    label,
    unit,
    keyboardType = "default",
    returnKeyType = "done",
    editable = true,
    onFocus,
    onChangeText
}: IInputProps) => {
    const { t } = dynamicTranslations();

    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = () => {
        setIsFocused(true);
        onFocus?.();
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    const handleChangeText = (text: string) => {
        onChangeText?.(text);
    };

    return (
        <View className="flex-col gap-2">
            {label && (
                <Text style={hasError ? baseStyles.errorLabel : baseStyles.normalLabel}>
                    {t(label)}
                </Text>
            )}
            <View style={[
                baseStyles.inputContainer,
                hasError ? baseStyles.error : isFocused ? baseStyles.focused : baseStyles.noStatus,
                !editable ? baseStyles.disabled : {}
            ]}>
                <TextInput
                    value={value}
                    style={[
                        baseStyles.input,
                        unit ? baseStyles.inputWithUnit : {},
                        !editable ? baseStyles.inputDisabled : {}
                    ]}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChangeText={handleChangeText}
                    keyboardType={keyboardType}
                    returnKeyType={returnKeyType}
                    editable={editable}
                />
                {unit && (
                    <Text style={[
                        baseStyles.unitText,
                        hasError ? baseStyles.unitTextError : isFocused ? baseStyles.unitTextFocused : baseStyles.unitTextNormal
                    ]}>
                        {unit}
                    </Text>
                )}
            </View>
            {hasError && errorMessage && (
                <Text style={baseStyles.errorText}>
                    {t(errorMessage)}
                </Text>
            )}
        </View>
    )
}

const baseStyles = StyleSheet.create({
    inputContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 2,
        borderRadius: 8,
        backgroundColor: 'white',
    },
    input: {
        flex: 1,
        paddingHorizontal: 8,
        paddingVertical: 8,
        fontSize: typography.fontSize.base,
        color: colorPalette.black,
        borderWidth: 0,
    },
    inputWithUnit: {
        paddingRight: 4,
    },
    noStatus: {
        borderColor: colorPalette.gray[300],
    },
    error: {
        borderColor: colorPalette.error[700],
    },
    focused: {
        borderColor: colorPalette.primary[700],
    },
    unitText: {
        fontSize: typography.fontSize.base,
        paddingHorizontal: 8,
        paddingVertical: 8,
        color: colorPalette.gray[500],
    },
    unitTextNormal: {
        color: colorPalette.gray[500],
    },
    unitTextFocused: {
        color: colorPalette.primary[700],
    },
    unitTextError: {
        color: colorPalette.error[700],
    },
    errorText: {
        color: colorPalette.error[700],
    },
    normalLabel: {
        color: colorPalette.black,
    },
    errorLabel: {
        color: colorPalette.error[700],
    },
    disabled: {
        backgroundColor: colorPalette.gray[100],
        borderColor: colorPalette.gray[200],
    },
    inputDisabled: {
        color: colorPalette.gray[400],
    }
})